import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Camera } from "lucide-react";
import SectionHeading from "@/components/SectionHeading";
import { galleryItems } from "@/data/content";

export default function Gallery() {
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && setLightbox(null);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <section id="galeria" data-testid="gallery-section" className="bg-brand-navy bg-noise py-20 sm:py-28 lg:py-32">
      <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-12">
        <SectionHeading
          dark
          chapter="04"
          kicker="Galeria"
          title="A pré-campanha em imagens"
          description="Visitas comunitárias, reuniões e ações de saúde pelo Extremo Sul. Espaço pronto para os registros oficiais da campanha."
        />

        <div className="masonry">
          {galleryItems.map((g, i) => (
            <motion.button
              key={i}
              type="button"
              initial={{ opacity: 0, y: 28 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ delay: (i % 3) * 0.1, duration: 0.6 }}
              data-testid={`gallery-item-${i}`}
              onClick={() => setLightbox(g)}
              className="group relative mb-5 block w-full overflow-hidden rounded-3xl text-left focus:outline-none focus:ring-2 focus:ring-brand-orange"
              aria-label={`Ampliar imagem: ${g.caption}`}
            >
              <img
                src={g.src}
                alt={g.caption}
                loading="lazy"
                className={`w-full object-cover transition-transform duration-500 group-hover:scale-105 ${g.tall ? "aspect-[3/4]" : "aspect-[4/3]"}`}
              />
              <div className="absolute inset-0 flex items-end bg-gradient-to-t from-brand-navy/80 via-transparent to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                <span className="flex items-center gap-2 p-5 text-sm font-bold text-white">
                  <Camera className="h-4 w-4 text-brand-orange" />
                  {g.caption}
                </span>
              </div>
            </motion.button>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {lightbox && (
          <motion.div
            data-testid="gallery-lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] flex items-center justify-center bg-black/90 p-4 sm:p-10"
            onClick={() => setLightbox(null)}
            role="dialog"
            aria-modal="true"
            aria-label="Visualização ampliada da galeria"
          >
            <button
              type="button"
              data-testid="gallery-lightbox-close"
              aria-label="Fechar visualização"
              onClick={() => setLightbox(null)}
              className="absolute right-5 top-5 rounded-full bg-white/10 p-3 text-white transition-colors hover:bg-white/20"
            >
              <X className="h-6 w-6" />
            </button>
            <motion.figure
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="max-h-full"
            >
              <img src={lightbox.src} alt={lightbox.caption} className="max-h-[80vh] w-auto rounded-2xl object-contain shadow-2xl" />
              <figcaption className="mt-4 text-center text-sm font-semibold text-white/80">{lightbox.caption}</figcaption>
            </motion.figure>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
